import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Output, OnInit, inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { SelectModule } from 'primeng/select';
import { CalendarModule } from 'primeng/calendar';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { finalize } from 'rxjs/operators';
import { toApiDateString } from '@shared/utils';
import { PeriodoRenovacaoMatriculaService } from '../../api/services/periodo-renovacao-matricula.service';
import {
  AnoLectivoOption,
  TIPO_BOLSA_OPTIONS,
  TipoBolsaOption,
} from '../../api/models/periodo-renovacao-matricula.model';

function dataFimAposDataInicioValidator(group: AbstractControl): ValidationErrors | null {
  const dataInicio = group.get('dataInicio')?.value;
  const dataFim = group.get('dataFim')?.value;
  if (!dataInicio || !dataFim) return null;
  return dataFim >= dataInicio ? null : { dataFimAntesDataInicio: true };
}

@Component({
  selector: 'app-periodo-renovacao-drawer',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    DrawerModule,
    SelectModule,
    CalendarModule,
    ConfirmDialogModule,
  ],
  // MessageService NÃO é fornecido aqui de propósito: o drawer é destruído (via *ngIf no
  // pai) no mesmo tick em que emite `closed`, o que destruiria também uma instância local
  // do MessageService antes de qualquer <p-toast/> local conseguir processar a mensagem.
  // Ao não o declarar, `inject(MessageService)` sobe na árvore de injectors até encontrar
  // o provider mais próximo (tipicamente a página que aloja o drawer, ou em último caso o
  // MessageService root fornecido em AppComponent) — cujo <p-toast/> sobrevive ao fecho.
  providers: [ConfirmationService, PeriodoRenovacaoMatriculaService],
  templateUrl: './periodo-renovacao-drawer.component.html',
  styleUrl: './periodo-renovacao-drawer.component.scss',
})
export class PeriodoRenovacaoDrawerComponent implements OnInit {
  /** true = período criado com sucesso (a listagem deve ser recarregada); false = fechado sem guardar. */
  @Output() closed = new EventEmitter<boolean>();

  private fb = inject(FormBuilder);
  private periodoService = inject(PeriodoRenovacaoMatriculaService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);

  visible = false;
  loading = false;
  form!: FormGroup;

  anosLectivos: AnoLectivoOption[] = [];
  tipoBolsaOptions: TipoBolsaOption[] = TIPO_BOLSA_OPTIONS;

  ngOnInit(): void {
    this.form = this.fb.group(
      {
        anoLectivo: [null, Validators.required],
        tipoBolsaEnum: [null, Validators.required],
        dataInicio: [null, Validators.required],
        dataFim: [null, Validators.required],
      },
      { validators: dataFimAposDataInicioValidator }
    );

    this.loadAnosLectivos();

    // Reset via *ngIf + abertura assíncrona: evita bugs de estado residual do p-sidebar do PrimeNG.
    setTimeout(() => (this.visible = true), 0);
  }

  private loadAnosLectivos(): void {
    this.periodoService.getAnosLectivos().subscribe({
      next: (anos) => {
        this.anosLectivos = (anos ?? []).filter((a) => a.activo === true);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível carregar os anos lectivos.',
        });
      },
    });
  }

  get minAnoLectivoDate(): Date | null {
    return this.anoLectivoLimitDate(Math.min);
  }

  get maxAnoLectivoDate(): Date | null {
    return this.anoLectivoLimitDate(Math.max);
  }

  private anoLectivoLimitDate(operation: (...values: number[]) => number): Date | null {
    const years = this.anosLectivos
      .map((item) => Number(item.ano))
      .filter((year) => Number.isInteger(year));

    return years.length ? new Date(operation(...years), 0, 1) : null;
  }

  /** Botão Cancelar / fecho pelo mask ou pelo X do sidebar. */
  requestClose(): void {
    if (this.form.dirty) {
      this.confirmationService.confirm({
        header: 'Alterações não guardadas',
        message: 'Tem alterações não guardadas. Deseja mesmo fechar sem guardar?',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Sim, fechar',
        rejectLabel: 'Continuar a editar',
        acceptButtonStyleClass: 'p-button-danger',
        accept: () => this.close(false),
      });
      return;
    }
    this.close(false);
  }

  private close(saved: boolean): void {
    this.visible = false;
    this.closed.emit(saved);
  }

  submit(): void {
    if (this.form.invalid || this.loading) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const selectedYear = raw.anoLectivo instanceof Date
      ? raw.anoLectivo.getFullYear().toString()
      : null;
    const anoLectivoId = this.anosLectivos.find((item) => item.ano === selectedYear)?.id;

    if (!anoLectivoId) {
      this.form.get('anoLectivo')?.setErrors({ anoLectivoInvalido: true });
      this.form.get('anoLectivo')?.markAsTouched();
      return;
    }

    const body = {
      anoLectivoId,
      dataInicio: toApiDateString(raw.dataInicio)!,
      dataFim: toApiDateString(raw.dataFim)!,
      tipoBolsaEnum: raw.tipoBolsaEnum,
    };

    this.loading = true;
    this.periodoService
      .criarPeriodo(body)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Período de renovação criado com sucesso.',
          });
          this.close(true);
        },
        error: (err) => {
          const mensagem =
            err?.error?.retorno?.mensagem ?? 'Não foi possível criar o período de renovação.';
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: mensagem,
          });
        },
      });
  }
}
